import { useEffect, useState } from "react";
import { RISK_LABEL, assessRisk, ruleOffer, subjectFromEvent, type Risk } from "@foreman/protocol";
import { useApp, useConnection } from "./ui";

type Asked = { tool?: string; detail?: string; risk?: Risk };

/** How risky an approval looks: what the computer said, else worked out here from what the card shows (the whole text, so it errs towards "check"). */
export function riskOf(a: Asked): Risk | null {
  if (a.risk) return a.risk;
  if (!a.tool) return null;
  return assessRisk(a.tool, a.detail);
}

/** "Check this · Pushes your commits". Careful is the one that stands out; nothing here is red. */
export function RiskChip({ risk }: { risk: Risk | null }) {
  if (!risk) return null;
  return (
    <span className="risk-chip" data-level={risk.level} title={RISK_LABEL[risk.level]}>
      <span className="risk-level">{RISK_LABEL[risk.level]}</span>
      <span className="risk-sep" aria-hidden>·</span>
      <span className="risk-what">{risk.headline}</span>
    </span>
  );
}

/**
 * Under an approval's Approve and Deny: "Always allow git status" and "Never allow". A tap saves the rule on the computer and then
 * answers this one the same way. Offered only when the computer can keep rules and a safe rule fits (never for anything careful).
 */
export function RuleActions({ computerId, asked, disabled, onDecide }: { computerId: string; asked: Asked; disabled?: boolean; onDecide: (allow: boolean) => Promise<void> | void }) {
  const { conn, snap } = useConnection(computerId);
  const { toast } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (snap.state === "ready" && snap.rules == null && !snap.rulesUnsupported) conn?.refreshRules();
  }, [snap.state, snap.rules, snap.rulesUnsupported, conn]);

  const risk = riskOf(asked);
  const offer = risk && snap.rules != null ? ruleOffer(subjectFromEvent(asked.tool, asked.detail), risk) : null;
  if (!conn || !offer || !risk) return null;
  // a rule that already exists answers this itself next time; don't offer what is already saved
  const exists = (effect: "allow" | "deny") => snap.rules!.some((r) => r.effect === effect && r.tool === offer.tool && r.prefix === offer.prefix);

  const save = (effect: "allow" | "deny") => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const ok = conn.addRule({ effect, tool: offer.tool, prefix: offer.prefix, label: offer.label }, (message) => {
      setError(message);
      setBusy(false);
    });
    if (!ok) {
      setError("Not connected to this computer.");
      return setBusy(false);
    }
    // the computer saves it before it reads the answer to this card (messages arrive in order)
    Promise.resolve(onDecide(effect === "allow"))
      .then(() => toast(effect === "allow" ? `Saved: always allow ${offer.label}. Change it in Settings.` : `Saved: never allow ${offer.label}. Change it in Settings.`))
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="rule-actions">
      {!exists("allow") && (
        <button type="button" className="rule-btn" disabled={disabled || busy} onClick={() => save("allow")}>
          Always allow <code>{offer.label}</code>
        </button>
      )}
      {!exists("deny") && (
        <button type="button" className="rule-btn" disabled={disabled || busy} onClick={() => save("deny")}>
          Never allow
        </button>
      )}
      {error && (
        <div className="ask-note bad" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
